/**
 * SOLFATA (Sep 25 2026): Cart auto-upgrade to The Complete Ritual.
 *
 * When the cart holds a standalone Anchor Kit AND a standalone mist, swap
 * that pair for the mist's own Complete Ritual bundle ($95 instead of $103,
 * and the bundle's comp-shipping tag makes the whole order ship free, which
 * is the store rule for any kit + mist order).
 *
 * Why theme-level: the store is on Basic, and a Cart Transform Function
 * needs Plus, so the swap runs here through the AJAX Cart API.
 *
 * Rules:
 * - Futureproof: no lineage list. A line counts as a mist when a product
 *   exists at <its-handle>-complete-ritual (the same convention
 *   blocks/smart-buy-box.liquid uses). No bundle, or bundle unavailable
 *   (e.g. Mounting Anchor stock at 0): do nothing.
 * - Pairing: one kit unit + one mist unit = one bundle. Kits pair with mists
 *   in the order the mist lines were added to the cart (oldest first; the
 *   AJAX cart lists newest first, so the list is walked in reverse). Mists
 *   whose bundle is unavailable are skipped and the kit tries the next one.
 *   Leftover kits or mists stay standalone.
 * - Safety: the bundle is added first, and the originals are trimmed only
 *   by the quantity Shopify actually added. Right before the trim the cart
 *   is re-read; if the paired lines changed in the meantime (the shopper
 *   edited the cart mid-swap), or the trim fails, the added bundles are
 *   rolled back. If the rollback also fails, auto-upgrade halts
 *   for the rest of the session so it can never double up.
 * - Loop guards: one check at a time, our own cart event is ignored, every
 *   check re-reads /cart.js, and a hard cap on swaps per page view.
 *
 * The confirmation banner lives in snippets/cart-ritual-upgrade.liquid and
 * is revealed by html[data-ritual-upgraded], which the cart's section morph
 * never touches.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const KIT_HANDLE = 'anchor-kit';
const BUNDLE_SUFFIX = '-complete-ritual';
const SOURCE = 'cart-ritual-upgrade';
const FLAG_KEY = 'solfata:ritual-upgraded';
const HALT_KEY = 'solfata:ritual-upgrade-halted';
const FLAG_ATTR = 'data-ritual-upgraded';
const MAX_SWAPS_PER_PAGE = 5;
const BUNDLE_CACHE_MS = 60 * 1000;
const CHECK_DELAY_MS = 350;

const config = window.SolfataRitualUpgrade || {};
const routesRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');

/** @type {WeakSet<Event>} */
const ownEvents = new WeakSet();
/** @type {Map<string, {at: number, value: Promise<{variantId: number} | null>}>} */
const bundleCache = new Map();

let running = false;
let rerun = false;
let swapsThisPage = 0;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let timer;

/* ---------- small helpers ---------- */

function storageGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key, value) {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch {
    /* private mode: the banner just won't persist across pages */
  }
}

function setUpgradedFlag(on) {
  storageSet(FLAG_KEY, on ? '1' : null);
  if (on) document.documentElement.setAttribute(FLAG_ATTR, 'true');
  else document.documentElement.removeAttribute(FLAG_ATTR);
}

async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `Cart request failed (${response.status})`);
  }
  return data;
}

async function getCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Failed to fetch cart (${response.status})`);
  return response.json();
}

function isTopLevel(item) {
  return !item.parent_relationship;
}

function isBundle(item) {
  return typeof item.handle === 'string' && item.handle.endsWith(BUNDLE_SUFFIX);
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/**
 * Resolves the Complete Ritual bundle for a mist handle by convention.
 * Resolves to null when there is no such product or it can't be bought.
 * @param {string} mistHandle
 */
function resolveBundle(mistHandle) {
  const cached = bundleCache.get(mistHandle);
  if (cached && Date.now() - cached.at < BUNDLE_CACHE_MS) return cached.value;

  const value = fetch(`${routesRoot}products/${encodeURIComponent(mistHandle + BUNDLE_SUFFIX)}.js`, {
    headers: { Accept: 'application/json' },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((product) => {
      if (!product || !product.available) return null;
      const variant = (product.variants || []).find((v) => v.available);
      return variant ? { variantId: variant.id } : null;
    })
    .catch(() => null);

  bundleCache.set(mistHandle, { at: Date.now(), value });
  return value;
}

/* ---------- planning ---------- */

/**
 * @typedef {{ key: string, quantity: number }} LineRef
 * @typedef {{ variantId: number, quantity: number, mists: LineRef[], handles: string[] }} BundleGroup
 */

/**
 * Works out which kit + mist units become which bundles.
 * @returns {Promise<{ groups: BundleGroup[], kitLines: LineRef[] } | null>}
 */
async function buildPlan(cart) {
  const items = (cart.items || []).filter(isTopLevel);
  const kitLines = items.filter((item) => item.handle === KIT_HANDLE);
  let kitUnits = kitLines.reduce((sum, item) => sum + item.quantity, 0);
  if (kitUnits === 0) return null;

  // Oldest first: the AJAX cart lists the most recently added line first.
  const candidates = items.filter((item) => item.handle !== KIT_HANDLE && !isBundle(item)).reverse();
  if (candidates.length === 0) return null;

  /** @type {Map<number, BundleGroup>} */
  const groups = new Map();
  for (const line of candidates) {
    if (kitUnits === 0) break;
    const bundle = await resolveBundle(line.handle);
    if (!bundle) continue;

    const quantity = Math.min(line.quantity, kitUnits);
    kitUnits -= quantity;

    const group = groups.get(bundle.variantId) || { variantId: bundle.variantId, quantity: 0, mists: [], handles: [] };
    group.quantity += quantity;
    group.mists.push({ key: line.key, quantity });
    if (!group.handles.includes(line.handle)) group.handles.push(line.handle);
    groups.set(bundle.variantId, group);
  }

  if (groups.size === 0) return null;

  return {
    groups: Array.from(groups.values()),
    kitLines: kitLines.reverse().map((item) => ({ key: item.key, quantity: item.quantity })),
  };
}

/* ---------- swap ---------- */

async function executeSwap(cart, plan) {
  const beforeByKey = new Map(cart.items.map((item) => [item.key, item.quantity]));

  /** @type {{ key: string, before: number }[]} */
  const added = [];
  /** @type {Map<string, number>} line key -> units to remove */
  const removals = new Map();
  let kitUnitsNeeded = 0;

  // 1. Add bundles first. Trim only what Shopify actually added.
  for (const group of plan.groups) {
    let addedLine;
    try {
      const response = await postJSON(Theme.routes.cart_add_url, {
        items: [{ id: group.variantId, quantity: group.quantity }],
      });
      addedLine = (response.items || [])[0];
    } catch (error) {
      console.warn('[ritual-upgrade] Bundle add failed, leaving that pair as is:', error);
      // Treat it as unavailable for a minute so a retry loop can't form.
      for (const handle of group.handles) bundleCache.set(handle, { at: Date.now(), value: Promise.resolve(null) });
      continue;
    }
    if (!addedLine) continue;

    const before = beforeByKey.get(addedLine.key) || 0;
    let delta = Math.max(0, Math.min(group.quantity, addedLine.quantity - before));
    beforeByKey.set(addedLine.key, addedLine.quantity);
    if (delta === 0) continue;
    added.push({ key: addedLine.key, before });

    kitUnitsNeeded += delta;
    for (const mist of group.mists) {
      if (delta === 0) break;
      const take = Math.min(mist.quantity, delta);
      removals.set(mist.key, (removals.get(mist.key) || 0) + take);
      delta -= take;
    }
  }

  if (added.length === 0) return false;

  for (const kit of plan.kitLines) {
    if (kitUnitsNeeded === 0) break;
    const take = Math.min(kit.quantity, kitUnitsNeeded);
    removals.set(kit.key, (removals.get(kit.key) || 0) + take);
    kitUnitsNeeded -= take;
  }

  const sections = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();

  // 2. Trim the originals, and render the cart sections in the same call.
  try {
    // Quantities come from a fresh read so a mid-swap edit is never undone.
    const fresh = await getCart();
    const freshQty = new Map(fresh.items.map((item) => [item.key, item.quantity]));
    /** @type {Record<string, number>} */
    const updates = {};
    for (const [key, units] of removals) {
      const current = freshQty.get(key) || 0;
      if (current < units) throw new Error('Paired lines changed during the upgrade');
      updates[key] = current - units;
    }

    const event = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
      promise: deferred.promise,
    });
    ownEvents.add(event);
    document.documentElement.dispatchEvent(event);

    const response = await postJSON(`${Theme.routes.cart_update_url}.js`, {
      updates,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    setUpgradedFlag(true);
    announce();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
      detail: {
        sections: response.sections ?? undefined,
        items: response.items,
        itemCount: response.item_count,
        source: SOURCE,
        didError: false,
      },
    });
    return true;
  } catch (error) {
    console.warn('[ritual-upgrade] Trim failed, rolling back the added bundles:', error);
    await rollback(added, deferred);
    return false;
  }
}

async function rollback(added, deferred) {
  /** @type {Record<string, number>} */
  const updates = {};
  for (const line of added) updates[line.key] = line.before;

  try {
    const response = await postJSON(`${Theme.routes.cart_update_url}.js`, {
      updates,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
      detail: {
        sections: response.sections ?? undefined,
        items: response.items,
        itemCount: response.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    // The cart is not empty or broken (the originals are all still there),
    // but it may hold an extra bundle. Stop auto-upgrading for this session
    // so nothing else stacks on top, and let the cart UI refresh itself.
    storageSet(HALT_KEY, '1');
    console.error('[ritual-upgrade] Rollback failed; auto-upgrade halted for this session:', error);
    deferred.reject(error);
  }
}

/* ---------- confirmation ---------- */

function announce() {
  const message = config.announcement;
  if (!message) return;
  let region = document.getElementById('ritual-upgrade-live');
  if (!region) {
    region = document.createElement('div');
    region.id = 'ritual-upgrade-live';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    document.body.appendChild(region);
  }
  region.textContent = '';
  // A timer, not requestAnimationFrame: rAF never fires in a background tab.
  setTimeout(() => {
    region.textContent = message;
  }, 150);
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-ritual-upgrade-dismiss]') : null;
  if (target) setUpgradedFlag(false);
});

/* ---------- scheduling ---------- */

async function runCheck() {
  if (running) {
    rerun = true;
    return;
  }
  if (storageGet(HALT_KEY) || swapsThisPage >= MAX_SWAPS_PER_PAGE) return;

  running = true;
  try {
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    const cart = await getCart();

    if (!(cart.items || []).some(isBundle)) setUpgradedFlag(false);

    const plan = await buildPlan(cart);
    if (plan && (await executeSwap(cart, plan))) swapsThisPage += 1;
  } catch (error) {
    console.warn('[ritual-upgrade] Check failed:', error);
  } finally {
    running = false;
    if (rerun) {
      rerun = false;
      scheduleCheck();
    }
  }
}

function scheduleCheck() {
  clearTimeout(timer);
  timer = setTimeout(runCheck, CHECK_DELAY_MS);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (ownEvents.has(event)) return;
  /** @type {CartLinesUpdateEvent} */ (event).promise
    ?.then((result) => {
      if (!result?.detail?.didError) scheduleCheck();
    })
    .catch(() => {});
});

// Cart drawer opening (covers carts changed in another tab).
document.querySelector('theme-drawer#cart-drawer')?.addEventListener(DrawerOpenEvent.eventName, scheduleCheck);

// Back/forward cache restores replay a stale page.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) scheduleCheck();
});

// Restore the banner on later pages, but drop a stale flag once the cart no
// longer holds a bundle (so a later manual bundle add never reads "Upgraded").
if (config.hasBundle === false) setUpgradedFlag(false);
else if (storageGet(FLAG_KEY)) document.documentElement.setAttribute(FLAG_ATTR, 'true');
if (config.checkOnLoad) scheduleCheck();
